import React, { useEffect, useState } from "react";
import ChatLeftSidebar from "./ChatLeftSidebar";
import UnifiedChat from "./UnifiedChat/index";
import { useChat } from "../../contexts/ChatContext";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import config from "../../config";
import { getApi } from "../AppApis";
import Header from "../Header/Header";

const Index = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { users } = useChat();
    const [sellerDetails, setSellerDetails] = useState(null);
    const [selectedChat, setSelectedChat] = useState("customerSupport");
    const [profileDetail, setProfileDetail] = useState({
        firstName: "",
        lastName: "",
        email: "",
        role: "",
        phone: "",
        userId: "",
        profile_pic: "",
    });
    const [recentChatUserList, setRecentChatUserList] = useState([]);
    const [recentCustomerChatUserList, setRecentCustomerChatUserList] = useState([]);
    const [userDocuments, setUserDocuments] = useState([]);
    const [messageValue, setMessageValue] = useState("");
    const [isMobile, setIsMobile] = useState(false);
    const [isChatListLoading, setIsChatListLoading] = useState(true);

    // Handle initial setup - only set messageValue here
    useEffect(() => {
        const chatId = searchParams.get("chat_id");

        // Only handle messageValue from location.state here
        if (location.state?.messageValue) {
            setMessageValue(location.state.messageValue);
        }

        // Default to customer support only if no chat_id and no processing pending
        if (!chatId) {
            setSelectedChat("customerSupport");
            setSellerDetails(null);
        }
    }, [searchParams]); // Removed location dependency to avoid conflicts

    useEffect(() => {
        document.title = `Chat | ${config.get("COMPANY_NAME")}`;
        if (window.innerWidth < 768) {
            setIsMobile(true);
        }
    }, []);
    document.title = `Chat | ${config.get("COMPANY_NAME")}`;

    useEffect(() => {
        if (sellerDetails && sellerDetails.property_id) {
            setSelectedChat(`${sellerDetails.seller_id}-${sellerDetails.property_id}`);
        }
    }, [sellerDetails]);

    const openUserChat = (e, chat, isProperty) => {
        e.preventDefault();
        if (!isProperty && chat.conversation_type === "customer_service") {
            setSelectedChat("customerSupport");
            // Navigate to /chat without chat_id for customer support
            navigate("/chat", { replace: true });
        } else {
            setSelectedChat(chat);
            // Navigate to /chat with chat_id parameter for specific chats
            const chatId = chat.id || chat.property_id || chat.seller_id;
            let url = `/chat?chat_id=${chatId}`;

            navigate(url, { replace: true });
        }
        setSellerDetails(chat);
    };

    // profile data
    useEffect(() => {
        const accessToken = localStorage.getItem("authUser");
        if (accessToken) {
            const token = accessToken.replace(/^"|"$/g, "");
            const profileUrl = `${config.get("API_URL")}/user/profile`;
            fetch(profileUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then((response) => response.json())
                .then((data) => {
                    setProfileDetail({
                        firstName: data.first_name,
                        lastName: data.last_name,
                        email: data.email,
                        role: data.role,
                        userId: data.uuid,
                        phone: data.phone,
                        profile_pic: data.profile_pic,
                    });
                })
                .catch((error) => {
                    console.error("Error fetching profile:", error);
                });
        }
    }, []);
    const fetchChatList = async (showLoading = true) => {
        if (showLoading) {
            setIsChatListLoading(true);
            // Add a minimum loading delay to show the skeleton only on initial load
            await new Promise((resolve) => setTimeout(resolve, 1500));
        }
        try {
            const response = await getApi("/messaging/conversations");
            if (response?.response?.data) {
                setRecentChatUserList(response?.response.data);

                // Check for chat_id in URL parameters
                let chatId = searchParams.get("chat_id");
                const propertyId = searchParams.get("property_id");

                if (chatId && !propertyId) {
                    // Find chat by ID or property_id (try original chatId first)
                    let chat = response?.response.data.find(
                        (c) => c.id === chatId || c.property_id === chatId || c.seller_id === chatId || `${c.seller_id}-${c.property_id}` === chatId
                    );

                    // If not found and chatId contains a dash, try extracting the parts
                    if (!chat && chatId.includes("-")) {
                        const parts = chatId.split("-");
                        const sellerId = parts[0];
                        const propId = parts[1];

                        chat = response?.response.data.find(
                            (c) =>
                                c.id === propId ||
                                c.property_id === propId ||
                                c.seller_id === sellerId ||
                                (c.seller_id === sellerId && c.property_id === propId)
                        );
                    }
                    if (chat) {
                        setSelectedChat(chat);
                        setSellerDetails(chat);
                    } else if (propertyId) {
                        // If no existing chat found but we have property_id, create new chat context
                        let newChatContext = { property_id: propertyId, isNewChat: true };

                        // Use location.state if available
                        if (location.state) {
                            newChatContext = { ...location.state, ...newChatContext };
                            if (location.state.messageValue) {
                                setMessageValue(location.state.messageValue);
                            }
                        } else {
                            // Check localStorage for property data (fallback for page refresh)
                            const latestProperty = localStorage.getItem("latestProperty");
                            const initialMessage = localStorage.getItem("initialMessage");
                            if (latestProperty) {
                                try {
                                    const property = JSON.parse(latestProperty);
                                    newChatContext = {
                                        ...newChatContext,
                                        property_data: property,
                                    };
                                    if (initialMessage) {
                                        setMessageValue(initialMessage);
                                    }
                                } catch (e) {
                                    console.warn("Could not parse property data from localStorage");
                                }
                            }
                        }

                        setSelectedChat(newChatContext);
                        setSellerDetails(newChatContext);
                    } else {
                        // If chat not found, default to customer support
                        setSelectedChat("customerSupport");
                        setSellerDetails(null);
                    }
                }

                // Handle legacy location.state for backwards compatibility (only if no URL params)
                if (!chatId && !propertyId && location.state?.chat_id) {
                    let chat_id = location.state.chat_id.split("-")[1];
                    const chat = response?.response.data.find((chat) => chat.id === chat_id);
                    if (chat) {
                        setSelectedChat(chat);
                        setSellerDetails(chat);
                        // Update URL to new format
                        navigate(`/chat?chat_id=${chat.id}`, { replace: true });
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching chat list:", error);
        } finally {
            if (showLoading) {
                setIsChatListLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchChatList();
    }, [searchParams]);

    // Silent chat list update (no loading indicators)
    const updateChatListSilently = async () => {
        try {
            const response = await getApi("/messaging/conversations");
            if (response?.response?.data) {
                setRecentChatUserList(response?.response.data);
                console.log("Chat list updated silently");
            }
        } catch (error) {
            console.error("Error updating chat list silently:", error);
        }
    };

    useEffect(() => {
        const accessToken = localStorage.getItem("authUser");
        if (accessToken) {
            const token = accessToken.replace(/^"|"$/g, "");
            const recentChatUserList = `${config.get("API_URL")}/messaging/conversations`;

            fetch(recentChatUserList, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
                .then((response) => response.json())
                .then((data) => {
                    setRecentCustomerChatUserList(data);
                })
                .catch((error) => {
                    console.error("Error fetching customer chat list:", error);
                });
        }
    }, []);

    return (
        <div
            style={{
                width: isMobile ? "" : "100%",
            }}
            // className="tw-flex tw-max-h-screen tw-overflow-hidden "
            className="chat-page-wrapper tw-max-h-screen tw-overflow-hidden "
        >
            <Header title={"Chat"} />
            <div className="chats-sidebar-container tw-shadow-md tw-shadow-black tw-mt-0 md:tw-pt-[55px]">
                <ChatLeftSidebar
                    sellerDetails={sellerDetails}
                    recentChatUserList={recentChatUserList}
                    recentCustomerChatUserList={recentCustomerChatUserList}
                    openUserChat={openUserChat}
                    setAllChats={setRecentChatUserList}
                    isChatListLoading={isChatListLoading}
                />
            </div>
            <div className="chats-container md:tw-pt-[55px]">
                {/* Unified chat component handles both customer support and buyer-seller chats */}
                <UnifiedChat
                    recentChatList={users}
                    selectedChat={selectedChat}
                    messageValue={messageValue}
                    sellerDetails={sellerDetails}
                    profileDetail={profileDetail}
                    setUserDocuments={setUserDocuments}
                    fetchChatList={fetchChatList}
                    updateChatListSilently={updateChatListSilently}
                />
            </div>
        </div>
    );
};

export default Index;
